import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'components/ui/button';
import { useTheme } from 'components/core/theme-provider';
import { useTranslation } from 'react-i18next';
import { getFileBlobUrl } from 'services/storage-service';
import { useConfig } from 'hooks/use-config';
import { openMediaLibraryFile } from '../utils/open-media-library-file';
import { useIsMobile } from 'hooks/use-mobile';
import { useIsMediaLibrary } from 'hooks/use-is-media-library';
import { ScrollArea } from 'components/ui/scroll-area';
import PDFViewer from './pdf-viewer';
import pdfIcon from 'assets/images/pdf-icon.svg';
import documentIcon from 'assets/images/document-icon.svg';
import csvIcon from 'assets/images/csv.svg';
import jsonIcon from 'assets/images/json.svg';
import powerpointIcon from 'assets/images/pptx.svg';
import vsdxIcon from 'assets/images/vsdx.svg';
import xlsxIcon from 'assets/images/xlsx.svg';

interface BaseFile {
    id: string | number;
    name: string;
    fileType: string;
    size?: string;
}

interface BaseCardProps<T extends BaseFile> {
    file: T;
    onViewDetails?: (file: T) => void;
    renderActions: (file: T) => React.ReactNode;
    IconComponent: React.ComponentType<{ className?: string }>;
    iconColor: string;
    backgroundColor: string;
    doubleClick?: (url: string, fileType: string) => void;
}

interface BaseGridViewProps<T extends BaseFile> {
    onViewDetails?: (file: T) => void;
    filters: Record<string, any>;
    data?: {
        data: T[];
        totalCount: number;
    };
    isLoading: boolean;
    error?: any;
    onLoadMore: () => void;
    renderDetailsSheet: (file: T | null, isOpen: boolean, onClose: () => void) => React.ReactNode;
    getFileTypeIcon: (fileType: string) => React.ComponentType<{ className?: string }>;
    getFileTypeInfo: (fileType: string) => { iconColor: string; backgroundColor: string };
    renderActions: (file: T) => React.ReactNode;
    emptyStateConfig: {
        icon: React.ComponentType<{ className?: string }>;
        title: string;
        description: string;
    };
    sectionLabels: {
        folder: string;
        file: string;
    };
    errorMessage: string;
    loadingMessage: string;
    loadMoreLabel: string;
    // Additional props for customization
    additionalFiles?: T[];
    filterFiles?: (files: T[], filters: Record<string, any>) => T[];
    processFiles?: (files: T[]) => T[];
    isDetailsOpen: boolean;
    selectedFile: T | null;
    handleCloseDetails: () => void;
}

// Common Card Component
const FileManagerCard = <T extends BaseFile>({
    file,
    onViewDetails,
    renderActions,
    IconComponent,
    iconColor,
    backgroundColor,
    doubleClick,
}: BaseCardProps<T>) => {
    const isFolder = file.fileType === 'Folder';
    const [fileUrl, setFileUrl] = useState<string | undefined>(undefined);
    const [loaded, setLoaded] = useState(false);

    const isFetchingRef = useRef(false);
    const previewPointerDownRef = useRef(false);

    useEffect(() => {
        if (file.fileType !== 'Photo') return;

        setLoaded(false);
        setFileUrl(undefined);

        async function getFileUrlAsync() {
            if (isFetchingRef.current) return;

            isFetchingRef.current = true;
            try {
                const url = await getFileUrl(file);
                setFileUrl(url);
            } catch (error) {
                console.error('Error fetching file URL:', error);
            } finally {
                isFetchingRef.current = false;
            }
        }
        getFileUrlAsync();
    }, [file.fileType, file.id, file.name]);

    const theme = useTheme();
    const isMediaLibrary = useIsMediaLibrary();
    const isPhoto = file.fileType === 'Photo' || file.fileType === 'Image';
    const isPdf = file.fileType === 'Pdf';
    const isGoogleDocsPreview =
        file.fileType === 'Document' ||
        file.fileType === 'Xlsx' ||
        file.fileType === 'Spreadsheet' ||
        file.fileType === 'Pptx' ||
        file.fileType === 'Csv';

    const handleCardClick = (e: React.MouseEvent) => {
        const target = e.target as HTMLElement;
        const isActionButton = target.closest('[data-action-button="true"]');

        if (!isActionButton) {
            e.preventDefault();
            onViewDetails?.(file);
        }
    };

    const handlePreview = async (e: React.MouseEvent) => {
        if (!isPhoto && !isPdf && !isGoogleDocsPreview) return;
        if ((e.target as HTMLElement).closest('[data-action-button="true"]')) return;
        e.preventDefault();
        if (isMediaLibrary && (isPdf || isGoogleDocsPreview)) {
            await openMediaLibraryFile(file, configurationSettings);
            return;
        }
        const url = fileUrl ?? (await getFileUrl(file));
        if (url) {
            doubleClick?.(url, isPhoto ? 'Photo' : file.fileType);
        }
    };

    const containerClasses = isMediaLibrary
        ? `group relative w-full cursor-pointer overflow-hidden rounded-[8px] bg-transparent text-left aspect-[256/341]${isPhoto ? ' border border-[#e7e7e7] dark:border-grey-400' : ''}`
        : 'group relative bg-card rounded-[8px] border border-gray hover:border-gray-200 hover:shadow-md transition-all duration-200 cursor-pointer h-[160px] overflow-hidden';
    // 'group relative bg-card rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all duration-200 cursor-pointer w-[213px] h-[160px]';
    const contentClasses = isFolder
        ? 'p-3 flex items-center space-x-3'
        : 'p-6 flex flex-col items-center text-center space-y-4';

    const iconContainerClasses = `${isFolder ? 'w-8 h-8' : 'w-20 h-20'} flex items-center justify-center ${isFolder ? backgroundColor : ''}`;
    // const iconClasses = `${isFolder ? 'w-5 h-5' : 'w-14 h-14'} ${iconColor}`;
    const renderFolderLayout = () => (
        <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
                <h3 className="text-sm font-medium text-gray-900 truncate" title={file.name}>
                    {file.name}
                </h3>
            </div>
            <section data-action-button="true" className="flex-shrink-0" aria-label="File actions">
                {renderActions(file)}
            </section>
        </div>
    );

    const renderFileLayout = (compact?: boolean) => (
        <div
            className={
                compact
                    ? 'flex items-center justify-between gap-2 p-1.5'
                    : 'flex items-center justify-between space-x-2 mt-2 p-1 bg-white rounded-b-[8px]'
            }
        >
            <div className="flex items-center space-x-2 flex-1 min-w-0">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center`}>
                    <IconComponent className={`w-4 h-4 ${iconColor}`} />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className={theme.theme === 'dark' ? 'text-sm font-medium text-gray truncate' : 'text-sm font-medium text-gray-900 truncate'} title={file.name}>
                        {file.name}
                    </h3>
                </div>
            </div>
            <section data-action-button="true" className="flex-shrink-0" aria-label="File actions">
                {renderActions(file)}
            </section>
        </div>
    );

    const footerVisibilityClasses = isFolder ? 'opacity-0 group-hover:opacity-100 transition-opacity duration-200' : 'opacity-100';

    const { configurationSettings } = useConfig();

    async function getFileUrl<T extends BaseFile>(file: T): Promise<string | undefined> {
        return getFileBlobUrl(file.id.toString(), configurationSettings, undefined);
    }

    const fileTypeIconMap: Record<string, string> = {
        Document: documentIcon,
        Pdf: pdfIcon,
        Xlsx: xlsxIcon,
        Pptx: powerpointIcon,
        Json: jsonIcon,
        Csv: csvIcon,
        Vsdx: vsdxIcon,
    };

    const filesImageViewer = (contained?: boolean) => {
        const fileIcon = fileTypeIconMap[file?.fileType] ?? documentIcon;
        return <div className="flex h-full w-full items-center justify-center rounded-lg">
            <img
                src={fileIcon}
                alt={file.name}
                className={contained
                    ? 'h-[80px] w-[68px] object-contain'
                    : 'h-full w-full rounded-[8px] object-cover transition-opacity duration-300'}
            />
        </div>;
    };

    if (isMediaLibrary) {
        return (
            <div
                className={containerClasses}
                onPointerDown={(e) => {
                    previewPointerDownRef.current = !(e.target as HTMLElement).closest('[data-action-button="true"]');
                }}
                onClick={
                    isPhoto || isPdf || isGoogleDocsPreview
                        ? (e) => {
                            if (!previewPointerDownRef.current) return;
                            previewPointerDownRef.current = false;
                            void handlePreview(e);
                        }
                        : undefined
                }
            >
                {isPhoto ? (
                    <>
                        <div className="absolute inset-0 bg-[#f6f6f6] dark:bg-muted">
                            {(!fileUrl || !loaded) && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <IconComponent className={`h-12 w-12 ${iconColor}`} />
                                </div>
                            )}
                            {fileUrl ? (
                                <img
                                    src={fileUrl}
                                    alt={file.name}
                                    onLoad={() => setLoaded(true)}
                                    className={`absolute inset-0 size-full rounded-[8px] object-cover transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
                                />
                            ) : null}
                        </div>
                        <div className="pointer-events-none absolute inset-0 z-[1] rounded-[8px] bg-[rgba(0,0,0,0.4)] opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-within:opacity-100 group-has-[[data-state=open]]:opacity-100" />
                        <div className="absolute inset-x-0 top-0 z-[2] px-5 py-4 pr-14 opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-within:opacity-100 group-has-[[data-state=open]]:opacity-100">
                            <div className="min-w-0 text-left text-[14px] leading-[1.2] text-white">
                                <p className="truncate font-semibold">{file.name}</p>
                                {file.size ? <p className="mt-1 font-normal">{file.size}</p> : null}
                            </div>
                        </div>
                        <section
                            data-action-button="true"
                            className="absolute right-3 top-3 z-[2] shrink-0 opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-within:opacity-100 group-has-[[data-state=open]]:opacity-100 [&_button]:text-white"
                        >
                            {renderActions(file)}
                        </section>
                    </>
                ) : (
                    <>
                        <div className="absolute inset-0 rounded-[8px] border border-[#e7e7e7] bg-white dark:border-grey-400 dark:bg-card" />
                        <div className="absolute inset-0 flex items-center justify-center">
                            {filesImageViewer(true)}
                        </div>
                        <div className="absolute inset-x-0 top-0 flex items-start justify-between px-5 py-4">
                            <div className="min-w-0 flex-1 text-left text-[14px] leading-[1.2] text-[#6d6d6d] dark:text-muted-foreground">
                                <p className="truncate font-semibold">{file.name}</p>
                                {file.size ? <p className="mt-1 font-normal">{file.size}</p> : null}
                            </div>
                            <section data-action-button="true" className="ml-2 shrink-0">
                                {renderActions(file)}
                            </section>
                        </div>
                    </>
                )}
            </div>
        );
    }

    return (
        <button
            className={containerClasses}
            onClick={handleCardClick}
            aria-label={`View details for ${file.name}`}
            type="button"
            onDoubleClick={handlePreview}
        >
            <div className="relative flex h-[160px] flex-col overflow-hidden rounded-[8px]">
                {file.fileType === 'Photo' ? (
                    <>
                        <div className="relative min-h-0 flex-1 w-full bg-muted/20">
                            {(!fileUrl || !loaded) && (
                                <div className="absolute inset-0 flex items-center justify-center rounded-t-[8px]">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="48" opacity="0.9" color='#dc3848' height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                                        strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-image-icon lucide-image">
                                        <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
                                        <circle cx="9" cy="9" r="2" />
                                        <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
                                    </svg>
                                </div>
                            )}
                            {fileUrl ? (
                                <img
                                    src={fileUrl}
                                    alt={file.name}
                                    onLoad={() => setLoaded(true)}
                                    className={`h-full w-full rounded-t-[8px] object-cover transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
                                />
                            ) : null}
                        </div>
                        <div className="shrink-0 border-t border-border bg-card">
                            {renderFileLayout(true)}
                        </div>
                    </>
                ) : (
                    <div className={`${contentClasses} h-[160px] relative flex justify-center items-center`}>
                        <div className={iconContainerClasses}>
                            {/* <IconComponent className={iconClasses} /> */}
                            {filesImageViewer()}
                        </div>
                        <div className={`${isFolder ? 'flex-1' : 'w-full'} absolute bottom-0 left-0 right-0 ${footerVisibilityClasses} bg-white/90 light:bg-dark-25`}>
                            {isFolder ? renderFolderLayout() : renderFileLayout()}
                        </div>
                    </div>
                )}
            </div>
        </button>
    );
};

export const FileManagerGridView = <T extends BaseFile>({
    onViewDetails,
    filters,
    data,
    isLoading,
    error,
    onLoadMore,
    renderDetailsSheet,
    getFileTypeIcon,
    getFileTypeInfo,
    renderActions,
    emptyStateConfig,
    sectionLabels,
    errorMessage,
    loadingMessage,
    loadMoreLabel,
    isDetailsOpen,
    selectedFile,
    handleCloseDetails,
    filterFiles,
    processFiles,
}: BaseGridViewProps<T>) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [selectedFileUrl, setSelectedFileUrl] = useState<string | undefined>(undefined);
    const [previewFileType, setPreviewFileType] = useState<string | undefined>(undefined);
    const isMediaLibrary = useIsMediaLibrary();

    const isMobile = useIsMobile();
    const shouldHideMainContent = isMobile && isDetailsOpen;

    const visibleFiles = useMemo<T[]>(() => {
        let files: T[] = (data?.data ?? []) as T[];
        if (processFiles) files = processFiles(files);
        if (filterFiles) files = filterFiles(files, filters) as T[];

        const fileTypeRaw = (filters as any)?.fileType;
        const fileType = typeof fileTypeRaw === 'string' ? fileTypeRaw.trim() : '';
        if (!fileType || fileType.toLowerCase() === 'all') return files;

        return files.filter((f: T) => (f.fileType || '').toLowerCase() === fileType.toLowerCase());
    }, [data?.data, filterFiles, filters, processFiles]);
    const hasActiveFilters = Object.values(filters).some(
        (value) =>
            value !== undefined &&
            value !== null &&
            value !== '' &&
            (Array.isArray(value) ? value.length > 0 : true)
    );

    if (error) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="text-center">
                    <p className="text-red-600 mb-2">{errorMessage}</p>
                </div>
            </div>
        );
    }

    if (isLoading && !data?.data.length) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4 text-primary"></div>
                    <p className="text-gray-500">{t(loadingMessage)}</p>
                </div>
            </div>
        );
    }
    return (
        <div className={isMediaLibrary ? 'relative flex h-full w-full' : 'relative flex h-full w-full rounded-xl overflow-hidden'}>
            {!shouldHideMainContent && (
                <div className="flex flex-col h-full w-full">
                    <ScrollArea className="h-full w-full">
                        <div className="space-y-8 pl-px pr-3 pt-px pb-4">
                            <div className="space-y-8 pb-4">
                                {visibleFiles.length > 0 && (
                                    <div>
                                        <div className={isMediaLibrary
                                            ? 'grid auto-rows-max grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
                                            : 'grid auto-rows-max grid-cols-1 gap-4 md:grid-cols-3'}>
                                            {visibleFiles.map((file: T) => {
                                                const IconComponent = getFileTypeIcon(file.fileType);
                                                const { iconColor, backgroundColor } = getFileTypeInfo(file.fileType);

                                                return (
                                                    <FileManagerCard
                                                        key={`${file.id}-${file.name}`}
                                                        file={file}
                                                        onViewDetails={onViewDetails}
                                                        renderActions={renderActions}
                                                        IconComponent={IconComponent}
                                                        iconColor={iconColor}
                                                        backgroundColor={backgroundColor}
                                                        doubleClick={(url, fileType) => {
                                                            setSelectedFileUrl(url);
                                                            setPreviewFileType(fileType);
                                                        }}
                                                    />
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {visibleFiles.length === 0 && !isLoading && (
                                    <div className="flex flex-col items-center justify-center p-12 text-center">
                                        <emptyStateConfig.icon className="h-12 w-12 text-gray-400 mb-4" />
                                        <h3 className={theme.theme === 'dark' ? 'text-lg font-medium text-gray mb-2' : 'text-lg font-medium text-gray-900 mb-2'}>{emptyStateConfig.title}</h3>
                                        <p className={theme.theme === 'dark' ? 'text-gray max-w-sm' : 'text-gray-500 max-w-sm'}>
                                            {hasActiveFilters
                                                ? 'No files match the current criteria'
                                                : emptyStateConfig.description}
                                        </p>
                                    </div>
                                )}

                                {data && data.data.length < data.totalCount && (
                                    <div className="flex justify-center pt-6">
                                        <Button
                                            onClick={onLoadMore}
                                            variant="outline"
                                            disabled={isLoading}
                                            className="min-w-32"
                                        >
                                            {isLoading ? (
                                                <div className="flex items-center gap-2">
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                                                    {t(loadingMessage)}
                                                </div>
                                            ) : (
                                                loadMoreLabel
                                            )}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                        {selectedFileUrl && (
                            <PDFViewer url={selectedFileUrl} setUrl={setSelectedFileUrl} fileType={previewFileType} />
                        )}
                    </ScrollArea>
                </div>
            )}
            {renderDetailsSheet(selectedFile, isDetailsOpen, handleCloseDetails)}
        </div>
    );
};
